import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { BankOutlined } from '@ant-design/icons';
import { Button, Switch } from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import { formatters } from 'services';
import {
  isDragDiagnosticsEnabled,
  setDragDiagnosticContext,
} from 'services/dragDiagnostics';
import ETFDetailsModal from 'reusable/ETFDetailsModal';
import {
  assignAssessmentNews,
  CREATING,
  displayNewsHeadline,
  fetchAssessmentState,
  fetchBlotter,
  findAFSBondMarketReplacementInstrument,
  FinancialInstrument,
  FROZEN,
  instrumentTypes,
  PAUSED,
  PLAYING,
  revokeAssessmentAssignment,
  selectors,
  SESSION_STATUSES,
  setAssessmentSlider,
  utils,
} from 'state';
import { fetchClientMoney } from '../../../state/app/client_money/actions';
import { fetchOptionsChains } from '../../../state/app/options/actions';
import { assessmentCaptureScope } from '../../../state/app/assessment/captureRequests';
import { TraineeSessionContext } from '../TraineeSessionContext';
import {
  activateExecution,
  idleExecution,
  type ExecutionKind,
} from '../MarketsTabV3/executionModel';
import {
  groupSession23Markets,
  instrumentHasPosition,
  sessionBlocksTrading,
} from '../MarketsTabV3/model';
import type { MarketInstrument } from '../MarketsTabV3/types';
import CashDeskTable from './CashDeskTable';
import CaptureStatusNotice from './CaptureStatusNotice';
import ExecutionWorkspace from './ExecutionWorkspace';
import InstrumentStack, { type InstrumentGroup } from './InstrumentStack';
import {
  filterMarketsV5HiddenInstruments,
  refreshOpenOrder,
  shouldDisplayMarketInstrument,
} from './model';
import NewsFeed from './NewsFeed';
import PriceGraph from './PriceGraph';

type Props = {
  assessmentPreviewEnabled?: boolean;
  animateHistoricalPriceUpdates?: boolean;
  fitInstrumentMarketValues?: boolean;
  showBondCountryMaturityLabel?: boolean;
  showBondDv01Summary?: boolean;
  hideInstrumentTicker?: boolean;
  showAvgOpenPrice?: boolean;
  showMarketClosedLabel?: boolean;
  hiddenUnderlyingAssetIds?: number[];
  heroProgress?: React.ReactNode;
  heroTotalSessionDays?: number;
  portfolioEvolution?: React.ReactNode;
};

const NO_HIDDEN_UNDERLYING_ASSET_IDS: number[] = [];

const MarketBoard: React.FC<Props> = ({
  assessmentPreviewEnabled = false,
  animateHistoricalPriceUpdates = false,
  fitInstrumentMarketValues = false,
  showBondCountryMaturityLabel = false,
  showBondDv01Summary = false,
  hideInstrumentTicker = false,
  showAvgOpenPrice = false,
  showMarketClosedLabel = false,
  hiddenUnderlyingAssetIds,
  heroProgress,
  heroTotalSessionDays,
  portfolioEvolution,
}) => {
  const dispatch = useDispatch();
  const traineeContext = useContext(TraineeSessionContext);
  const session = useSelector(selectors.activeSession);
  const player = useSelector(selectors.player);
  const assets = useSelector(selectors.underlyingAssets);
  const assetList = useSelector(selectors.underlyingAssetsList);
  const currencies = useSelector(selectors.currencies);
  const currencyList = useSelector(selectors.currenciesList);
  const marketHours = useSelector(selectors.marketHours);
  const instruments = useSelector(
    selectors.playerMarketFinancialInstrumentsList,
  );
  const clientsFetched = useSelector(selectors.clientsFetched);
  const hasLegacyOptions = useSelector(selectors.sessionHasOptions);
  const assessment = useSelector(selectors.assessment);
  const assessmentAssignments = useSelector(selectors.assessmentAssignments);
  const assessmentInstruments = useSelector(selectors.assessmentInstruments);
  const assessmentSliders = useSelector(selectors.assessmentSliders);
  const news = useSelector(selectors.newsList);
  const lastNews = useSelector(selectors.lastNews);
  const deliveredCurrentPeriodNewsIds = useSelector(
    selectors.currentPeriodNewsIds,
  );
  const { fetched_blotter: blotterFetched } = player;
  const reportingCurrency = currencies[session.reporting_currency];
  const reportingCurrencySymbol = reportingCurrency?.symbol || '';

  const [positionsOnly, setPositionsOnly] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState<
    FinancialInstrument | undefined
  >();
  const [executionIntent, setExecutionIntent] = useState(idleExecution);
  const [detailsAssetId, setDetailsAssetId] = useState<number>();

  useEffect(() => {
    document.body.classList.add('markets-v4-is-active');
    return () => document.body.classList.remove('markets-v4-is-active');
  }, []);

  const openExecution = useCallback(
    (kind: ExecutionKind, instrumentId?: number) =>
      setExecutionIntent(current =>
        activateExecution(current, kind, instrumentId),
      ),
    [],
  );
  const closeExecution = useCallback(
    () => setExecutionIntent(current => idleExecution(current.activation + 1)),
    [],
  );
  const resetOpenOrder = useCallback(
    () => setExecutionIntent(refreshOpenOrder),
    [],
  );
  const openCashExchange = useCallback(() => {
    setSelectedInstrument(undefined);
    openExecution('exchange');
  }, [openExecution]);

  useEffect(() => {
    if (!blotterFetched) dispatch(fetchBlotter({}));
  }, [blotterFetched, dispatch]);

  useEffect(() => {
    if (!session.id) return;
    dispatch(fetchAssessmentState({}));
  }, [
    dispatch,
    lastNews,
    player.id,
    session.elapsed_days,
    session.id,
    session.status,
  ]);

  useEffect(() => {
    if (session.client_money && !clientsFetched) {
      dispatch(fetchClientMoney({ session_id: session.id }));
    }
    if (hasLegacyOptions) {
      dispatch(fetchOptionsChains({ session_id: session.id }));
    }
  }, [
    clientsFetched,
    dispatch,
    hasLegacyOptions,
    session.client_money,
    session.id,
  ]);

  const effectiveHiddenUnderlyingAssetIds =
    hiddenUnderlyingAssetIds ?? NO_HIDDEN_UNDERLYING_ASSET_IDS;
  const hiddenUnderlyingAssetIdSet = useMemo(
    () => new Set(effectiveHiddenUnderlyingAssetIds),
    [effectiveHiddenUnderlyingAssetIds],
  );
  const displayedInstruments = useMemo(
    () =>
      filterMarketsV5HiddenInstruments(
        instruments,
        effectiveHiddenUnderlyingAssetIds,
      ),
    [effectiveHiddenUnderlyingAssetIds, instruments],
  );
  const groupedInstruments = useMemo(
    () => groupSession23Markets(displayedInstruments, assets),
    [assets, displayedInstruments],
  );

  const rowsByInstrumentId = useMemo(() => {
    const rows = new Map<number, MarketInstrument>();
    instruments.forEach(instrument => {
      const asset = assets[instrument.underlying_asset];
      const currency = asset && currencies[asset.base_currency];
      if (!asset || !currency || !reportingCurrency) return;
      const [marketIsOpen, closedFromInterval] = utils.assetIsOpenNew(
        asset,
        session,
        marketHours[asset.market_hours],
      );
      rows.set(instrument.id, {
        instrument,
        asset,
        currencySymbol: currency.symbol,
        reportingValue: utils.reportingValue(
          instrument,
          currency,
          asset.resourcetype,
          assetList,
          currencyList,
          reportingCurrency,
        ),
        marketIsOpen,
        closedFromInterval,
      });
    });
    return rows;
  }, [
    assetList,
    assets,
    currencies,
    currencyList,
    instruments,
    marketHours,
    reportingCurrency,
    session,
  ]);

  const rowsFor = useCallback(
    (items: FinancialInstrument[]) =>
      items
        .filter(item => !positionsOnly || instrumentHasPosition(item))
        .map(item => rowsByInstrumentId.get(item.id))
        .filter((row): row is MarketInstrument => Boolean(row))
        .filter(row =>
          shouldDisplayMarketInstrument(row.instrument, row.asset),
        ),
    [positionsOnly, rowsByInstrumentId],
  );

  const etfTypes = new Set(
    groupedInstruments.etfs.map(
      instrument => assets[instrument.underlying_asset]?.resourcetype,
    ),
  );
  const etfLabel =
    etfTypes.size === 1 && etfTypes.has('ETFCommodities')
      ? 'ETF Commodities'
      : 'ETFs';

  const instrumentGroups = useMemo<InstrumentGroup[]>(
    () =>
      [
        groupedInstruments.fx.length
          ? {
              key: 'fx' as const,
              label: 'FX',
              rows: rowsFor(groupedInstruments.fx).sort((a, b) =>
                a.asset.ticker.localeCompare(b.asset.ticker),
              ),
            }
          : null,
        groupedInstruments.etfs.length
          ? {
              key: 'etf' as const,
              label: etfLabel,
              rows: rowsFor(groupedInstruments.etfs),
            }
          : null,
        groupedInstruments.equities.length
          ? {
              key: 'equity' as const,
              label: 'Equities',
              rows: rowsFor(groupedInstruments.equities).sort((a, b) =>
                a.asset.ticker.localeCompare(b.asset.ticker),
              ),
            }
          : null,
        groupedInstruments.options.length
          ? {
              key: 'option' as const,
              label: 'Options',
              rows: rowsFor(groupedInstruments.options),
            }
          : null,
        groupedInstruments.bonds.length
          ? {
              key: 'bond' as const,
              label: 'Bonds',
              rows: rowsFor(groupedInstruments.bonds).sort((a, b) =>
                (a.asset.asset_issue_date || '').localeCompare(
                  b.asset.asset_issue_date || '',
                ),
              ),
            }
          : null,
      ].filter((group): group is InstrumentGroup =>
        Boolean(group?.rows.length),
      ),
    [etfLabel, groupedInstruments, rowsFor],
  );

  const selectInstrument = useCallback(
    (instrument: FinancialInstrument) => {
      const asset = assets[instrument.underlying_asset];
      const typeIndex = instrumentTypes.findIndex(
        item => item.type === asset?.resourcetype,
      );
      if (typeIndex >= 0) traineeContext.setInstrumentType(`${typeIndex}`);
      traineeContext.setShowCash(false);
      setSelectedInstrument(instrument);
    },
    [assets, traineeContext],
  );

  const openInstrumentOrder = useCallback(
    (instrument: FinancialInstrument) => {
      selectInstrument(instrument);
      openExecution(
        instrument.simplified_option ? 'optionOrder' : 'order',
        instrument.id,
      );
    },
    [openExecution, selectInstrument],
  );

  useEffect(() => {
    if (executionIntent.kind === 'exchange') return;
    const displayedRows = instrumentGroups.flatMap(group => group.rows);
    const executionTargetHidden =
      'instrumentId' in executionIntent &&
      hiddenUnderlyingAssetIdSet.has(
        rowsByInstrumentId.get(executionIntent.instrumentId)?.asset.id ?? -1,
      );
    if (executionTargetHidden) closeExecution();

    if (
      selectedInstrument &&
      hiddenUnderlyingAssetIdSet.has(selectedInstrument.underlying_asset)
    ) {
      closeExecution();
      const first = displayedRows[0];
      if (first) selectInstrument(first.instrument);
      else setSelectedInstrument(undefined);
      return;
    }
    if (
      selectedInstrument &&
      instrumentGroups.some(group =>
        group.rows.some(row => row.instrument.id === selectedInstrument.id),
      )
    ) {
      return;
    }
    const replacement = findAFSBondMarketReplacementInstrument(
      selectedInstrument,
      displayedRows.map(row => row.instrument),
      session.afs_bond_market_replacements,
    );
    if (replacement) {
      closeExecution();
      selectInstrument(replacement);
      return;
    }
    const first = displayedRows[0];
    if (first) openInstrumentOrder(first.instrument);
  }, [
    closeExecution,
    executionIntent.kind,
    hiddenUnderlyingAssetIdSet,
    instrumentGroups,
    instruments,
    openInstrumentOrder,
    selectInstrument,
    selectedInstrument,
    session.afs_bond_market_replacements,
  ]);

  useEffect(() => {
    if (
      detailsAssetId !== undefined &&
      hiddenUnderlyingAssetIdSet.has(detailsAssetId)
    ) {
      setDetailsAssetId(undefined);
    }
  }, [detailsAssetId, hiddenUnderlyingAssetIdSet]);

  const visibleSelectedInstrument =
    selectedInstrument &&
    !hiddenUnderlyingAssetIdSet.has(selectedInstrument.underlying_asset)
      ? selectedInstrument
      : undefined;
  const executionTargetHidden =
    'instrumentId' in executionIntent &&
    hiddenUnderlyingAssetIdSet.has(
      rowsByInstrumentId.get(executionIntent.instrumentId)?.asset.id ?? -1,
    );
  const visibleExecutionIntent = executionTargetHidden
    ? idleExecution(executionIntent.activation)
    : executionIntent;
  const selectedRow = visibleSelectedInstrument
    ? rowsByInstrumentId.get(visibleSelectedInstrument.id)
    : undefined;
  const executionRow =
    'instrumentId' in visibleExecutionIntent
      ? rowsByInstrumentId.get(visibleExecutionIntent.instrumentId)
      : selectedRow;
  const displayedCount = instrumentGroups.reduce(
    (count, group) => count + group.rows.length,
    0,
  );
  const activePositions = instruments.filter(instrumentHasPosition).length;
  const newsHeadlines = useMemo(
    () =>
      news.reduce<Record<number, string>>((headlines, item) => {
        headlines[item.id] = displayNewsHeadline(item);
        return headlines;
      }, {}),
    [news],
  );
  const currentNewsIds = useMemo(() => {
    const newsIds =
      deliveredCurrentPeriodNewsIds ?? Object.keys(lastNews || {}).map(Number);
    return new Set(
      newsIds.filter(newsId => Number.isInteger(newsId) && newsId > 0),
    );
  }, [deliveredCurrentPeriodNewsIds, lastNews]);
  const assessmentUiInstruments = useMemo(() => {
    if (assessmentInstruments.length) return assessmentInstruments;
    if (!assessmentPreviewEnabled) return [];

    const seenAssets = new Set<number>();
    return instrumentGroups.flatMap(group =>
      group.rows.flatMap(row => {
        if (row.instrument.simplified_option || seenAssets.has(row.asset.id)) {
          return [];
        }
        seenAssets.add(row.asset.id);
        return [
          {
            code: row.asset.ticker,
            kind: 'rates' as const,
            parent: null,
            underlying_asset: row.asset.id,
          },
        ];
      }),
    );
  }, [assessmentInstruments, assessmentPreviewEnabled, instrumentGroups]);
  const captureScope = assessmentCaptureScope(
    session.id,
    player.id,
    session.elapsed_days,
  );
  const captureScopeMatches =
    assessment.capture_scope === undefined ||
    assessment.capture_scope === captureScope;
  const persistAssessmentWrites =
    assessment.attached && session.status === PLAYING && captureScopeMatches;
  const isTradingBlocked = sessionBlocksTrading(session.status);
  useEffect(() => {
    if (!isDragDiagnosticsEnabled()) return;
    setDragDiagnosticContext({
      session_id: session.id,
      player_id: player.id,
      period: session.elapsed_days,
      session_status: session.status,
      attached: assessment.attached,
      can_write: assessmentPreviewEnabled || persistAssessmentWrites,
      persist_writes: persistAssessmentWrites,
      news_count: news.length,
      current_news_count: deliveredCurrentPeriodNewsIds?.length,
      instrument_count: assessmentInstruments.length,
      assignment_count: assessmentAssignments.length,
      pending_count: assessmentAssignments.filter(item => item.pending).length,
    });
  }, [
    session.id,
    player.id,
    session.elapsed_days,
    session.status,
    assessment.attached,
    assessmentPreviewEnabled,
    persistAssessmentWrites,
    news.length,
    deliveredCurrentPeriodNewsIds?.length,
    assessmentInstruments.length,
    assessmentAssignments,
  ]);
  const currentSessionDay = Math.max(1, session.elapsed_days + 1);

  const hero = (
    <header className="markets-v2-hero markets-v4-hero">
      <div className="markets-v2-hero__copy markets-v4-hero__summary">
        {heroProgress ? (
          <div className="markets-v4-hero__session-line">
            <div className="markets-v2-hero__eyebrow">
              Session {session.id} · Day {currentSessionDay}
              {heroTotalSessionDays !== undefined
                ? ` / ${heroTotalSessionDays}`
                : ''}
            </div>
            {heroProgress}
          </div>
        ) : (
          <div className="markets-v2-hero__eyebrow">
            Session {session.id} · Day {currentSessionDay}
          </div>
        )}
        <div className="markets-v2-hero__stats markets-v4-hero__stats">
          <div>
            <span>Market value</span>
            <strong>{formatters.million(player.last_market_value)}</strong>
            <small>{reportingCurrencySymbol}</small>
          </div>
          <div>
            <span>Portfolio return</span>
            <strong>
              {formatters.percent2Digit(player.last_portfolio_return)}
            </strong>
          </div>
          <div>
            <span>Open positions</span>
            <strong>{activePositions}</strong>
          </div>
        </div>
      </div>

      <div className="markets-v2-hero__desk">
        <div className="markets-v2-hero__topline">
          <span
            className={`markets-v2-session-state ${
              isTradingBlocked ? 'is-blocked' : 'is-running'
            }`}
          >
            <i />
            {SESSION_STATUSES[session.status]}
          </span>
        </div>
      </div>
    </header>
  );

  return (
    <div className="markets-v2 markets-v4">
      <div className="markets-v4-hero-row">
        {portfolioEvolution ? (
          <div className="markets-v4-hero-split">
            {hero}
            {portfolioEvolution}
          </div>
        ) : (
          hero
        )}
        <div className="markets-v4-hero-reserved">
          <PriceGraph
            instrument={visibleSelectedInstrument}
            animateHistoricalPriceUpdates={animateHistoricalPriceUpdates}
          />
        </div>
      </div>

      <CaptureStatusNotice
        sessionStatus={session.status}
        canWrite={persistAssessmentWrites}
        fetching={assessment.fetching}
        fetched={assessment.fetched}
        scopeMatches={captureScopeMatches}
        loadFailed={assessment.capture_error_scope === captureScope}
        preview={assessmentPreviewEnabled}
        retry={() => dispatch(fetchAssessmentState({}))}
      />

      <div className="markets-v4-workspace">
        <NewsFeed
          dragEnabled={assessmentPreviewEnabled || persistAssessmentWrites}
        />

        <div className="markets-v4-market-column">
          <section
            className="markets-v4-instruments"
            aria-label="All instruments"
          >
            <header className="markets-v4-instruments__head">
              <div>
                <h2>Instruments</h2>
                <span>{displayedCount} shown</span>
              </div>
              <label>
                Positions only
                <Switch checked={positionsOnly} onChange={setPositionsOnly} />
              </label>
            </header>
            <div className="markets-v4-instruments__body">
              <InstrumentStack
                groups={instrumentGroups}
                session={session}
                reportingCurrencySymbol={reportingCurrencySymbol}
                selectedInstrumentId={visibleSelectedInstrument?.id}
                selectInstrument={selectInstrument}
                openInstrumentOrder={openInstrumentOrder}
                executionIntent={visibleExecutionIntent}
                openODA={id => openExecution('oda', id)}
                showETFDetails={setDetailsAssetId}
                fitMarketValues={fitInstrumentMarketValues}
                showBondCountryMaturityLabel={showBondCountryMaturityLabel}
                bondDv01Snapshot={
                  showBondDv01Summary
                    ? (player.latest_afs_dv01 ?? null)
                    : undefined
                }
                hideInstrumentTicker={hideInstrumentTicker}
                assessment={{
                  attached: assessment.attached || assessmentPreviewEnabled,
                  canWrite: persistAssessmentWrites || assessmentPreviewEnabled,
                  persistWrites: persistAssessmentWrites,
                  currentPeriod:
                    assessment.current_period ?? session.elapsed_days,
                  scenario: assessment.scenario || 'v5-preview',
                  playerId: player.id,
                  instruments: assessmentUiInstruments,
                  assignments: assessmentAssignments,
                  sliders: assessmentSliders,
                  headlines: newsHeadlines,
                  currentNewsIds,
                  assignNews: payload =>
                    dispatch(assignAssessmentNews(payload)),
                  revokeAssignment: assignment =>
                    dispatch(revokeAssessmentAssignment({ assignment })),
                  setSlider: (instrumentCode, value, previousValue) =>
                    dispatch(
                      setAssessmentSlider({
                        instrument_code: instrumentCode,
                        value,
                        prev_value: previousValue,
                      }),
                    ),
                }}
              />
            </div>
          </section>

          <section className="markets-v4-cash-desk" aria-label="Cash desk">
            <header className="markets-v4-cash-desk__head">
              <h3>
                <BankOutlined /> Cash desk
              </h3>
              <Button
                size="small"
                icon={<BankOutlined />}
                disabled={
                  session.status === PAUSED ||
                  session.status === CREATING ||
                  session.status === FROZEN
                }
                className={
                  executionIntent.kind === 'exchange' ? 'is-in-desk' : ''
                }
                aria-pressed={executionIntent.kind === 'exchange'}
                onClick={openCashExchange}
              >
                Exchange
              </Button>
            </header>
            <CashDeskTable />
          </section>
        </div>

        <ExecutionWorkspace
          intent={visibleExecutionIntent}
          selectedRow={executionRow}
          reportingCurrencySymbol={reportingCurrencySymbol}
          showAvgOpenPrice={showAvgOpenPrice}
          showMarketClosedLabel={showMarketClosedLabel}
          close={closeExecution}
          completeOrder={resetOpenOrder}
        />
      </div>

      {detailsAssetId !== undefined &&
        !hiddenUnderlyingAssetIdSet.has(detailsAssetId) && (
          <ETFDetailsModal
            assetId={detailsAssetId}
            visible={detailsAssetId !== undefined}
            hide={() => setDetailsAssetId(undefined)}
          />
        )}
    </div>
  );
};

export default MarketBoard;
